import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Collapse,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  InputBase,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ExpandMore,
  ExpandLess,
  WarningAmber as IconWarning,
  LooksOne as IconFirstTime,
  OpenInNew as OpenIcon,
  PhotoCamera as IconPhoto,
} from "@mui/icons-material";
import { notify } from "../../services/ui/notify";
import { ActivityIndicator, View } from "react-native";
import { urlExiste } from "../../services/Functions";
import { SCREEN_IDS } from "../../constants/Constants.js";
import { typeConnect } from "../../constants/Constants.js";
import {
  getReferencias,
  insertNewTheoreticalTime,
} from "../../services/ConnectDataBase";
import { BACKGROUND_COLORS, TEXT_COLORS, STATUS_COLORS, BRAND_COLORS, LOADING_COLORS, BORDER_COLORS  } from "../../style/colors.js";

// Configuración de skeletons de carga
const SKELETON_CONFIG = {
  rows: 8,              // Número de filas de skeletons
  height: 20,           // Altura de los skeletons en px
  widthLabel: "100%",    // Ancho del skeleton de etiqueta
  widthValue: "100%",    // Ancho del skeleton de valor
};

const VistaAvanzadaReferencias = ({
  searchTerm,
  setSearchTerm,
  filterRealSinStand,
  filterFirstTime,
  referenciasActualizadas,
  detalleDisponible,
  onOpenModal,
}) => {
  const [todasLasReferencias, setTodasLasReferencias] = useState({});
  const [procesosPorReferencia, setProcesosPorReferencia] = useState({});
  const [referenciaExpandida, setReferenciaExpandida] = useState(null);
  const [inputsValores, setInputsValores] = useState({});
  const [metalSeleccionadoPorProceso, setMetalSeleccionadoPorProceso] =
    useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const [openImage, setOpenImage] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState({});
  const [highlightKey, setHighlightKey] = useState(null);
  const [searchTermLocal, setSearchTermLocal] = useState(searchTerm || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    searchTerm || ""
  );

  const [fechaSeleccionada, setFechaSeleccionada] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const [procesoSeleccionado, setProcesoSeleccionado] = useState("TODOS");

  const [imageUrls, setImageUrls] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("imageUrls")) || {};
    } catch {
      return {};
    }
  });

  const procesosDisponibles = useMemo(() => {
    const procesos = new Set();
    Object.keys(detalleDisponible).forEach((key) => {
      const [, proceso] = key.split("|");
      if (proceso) {
        procesos.add(proceso);
      }
    });
    return ["TODOS", ...Array.from(procesos).sort()];
  }, [detalleDisponible]);

  const porPagina = 8;

  // Combinamos todas las referencias de todos los metales
  const referencias = useMemo(() => {
    const todasReferencias = {};
    Object.keys(todasLasReferencias).forEach((metal) => {
      Object.assign(todasReferencias, todasLasReferencias[metal] || {});
    });
    return todasReferencias;
  }, [todasLasReferencias]);

  useEffect(() => {
    const fetchReferencias = async () => {
      try {
        setIsLoading(true);
        const response = await getReferencias(
          "referencias",
          typeConnect[0],
          SCREEN_IDS.GENERAL
        );

        if (response && response.status === "success") {
          setTodasLasReferencias(response.data);
        }
      } catch (error) {
        console.error("Error cargando referencias:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferencias();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTermLocal);
      if (setSearchTerm) {
        setSearchTerm(searchTermLocal);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTermLocal, setSearchTerm]);

  useEffect(() => {
    if (searchTerm !== searchTermLocal) {
      setSearchTermLocal(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    setPaginaActual(1);
  }, [
    searchTerm,
    filterRealSinStand,
    filterFirstTime,
    fechaSeleccionada,
    procesoSeleccionado,
  ]);

  useEffect(() => {
    if (!referencias || !Object.keys(referencias).length) return;

    const CACHE_KEY = "imageUrls";
    const TS_KEY = "imageUrlsTimestamp";
    const MILIS_24H = 24 * 60 * 60 * 1000;
    const now = Date.now();

    let savedUrls = {};
    try {
      savedUrls = JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
    } catch {}

    const savedTs = Number(localStorage.getItem(TS_KEY)) || 0;
    const expired = now - savedTs > MILIS_24H;

    if (!savedTs || expired) {
      const loadImageUrls = async () => {
        const newCache = {};
        const referencias_keys = Object.keys(referencias);

        await Promise.all(
          referencias_keys.map(async (ref) => {
            const data = referencias[ref];
            if (data.FullRef && Array.isArray(data.FullRef)) {
              for (const full of data.FullRef) {
                const url = `https://catalog.facetbarcelona.com/images/database-odoo/FM/${full}.jpg`;
                try {
                  if (await urlExiste(url)) {
                    newCache[ref] = url;
                    break;
                  }
                } catch (error) {
                  console.error(`Error checking URL for ${ref}:`, error);
                }
              }
            }
          })
        );

        setImageUrls(newCache);
        localStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
        localStorage.setItem(TS_KEY, now.toString());

        console.log("Nuevas URLs cargadas:", newCache);
      };

      loadImageUrls();
    } else {
      setImageUrls(savedUrls);
      console.log("URLs cargadas desde cache:", savedUrls);
    }
  }, [referencias]);

  const handleOpenImage = (url) => {
    setOpenImage(url);
  };

  const handleCloseImage = () => {
    setOpenImage(null);
  };

  const lookupActualizadas = useMemo(() => {
    const map = {};
    referenciasActualizadas.forEach((ref) => {
      const prefijo = ref.referencia.substring(0, 5);
      const key = `${prefijo}-${ref.codigo}`;
      map[key] = ref;
    });
    return map;
  }, [referenciasActualizadas]);

  const convertirFecha = (fechaString) => {
    if (!fechaString || typeof fechaString !== "string") return null;
    const [dd, mm, yyyy] = fechaString.split("-");
    if (!dd || !mm || !yyyy) return null;
    return `${yyyy}-${mm}-${dd}`;
  };

  const referenciasFiltradas = useMemo(() => {
    let arr = Object.entries(referencias);

    if (fechaSeleccionada) {
      arr = arr.filter(([ref, data]) => {
        return Object.keys(detalleDisponible).some((key) => {
          const [refKey, proceso] = key.split("|");

          if (refKey === ref && referencias[refKey]) {
            const cumpleProceso =
              procesoSeleccionado === "TODOS" ||
              proceso === procesoSeleccionado;

            if (cumpleProceso) {
              const detalle = detalleDisponible[key];
              if (detalle && detalle.fechas) {
                return detalle.fechas.some((fecha) => {
                  const fechaConvertida = convertirFecha(fecha);
                  return fechaConvertida === fechaSeleccionada;
                });
              }
            }
          }
          return false;
        });
      });
    }

    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.trim().toLowerCase();
      arr = arr.filter(([ref]) => {
        const refPrefix = ref.substring(0, 5).toLowerCase();
        const searchTerm = term.substring(0, 5);
        return refPrefix.startsWith(searchTerm);
      });
    }

    if (filterRealSinStand) {
      arr = arr.filter(([ref, data]) => {
        const procesos = data.procesos;
        const allStdZero =
          data.hasRealData === "Y" &&
          procesos.every((p) => parseFloat(p.numeropiedras || 0) === 0);

        const hasChildWarning = procesos.some((p) => {
          const detailKey = `${ref}|${p.descripcion}`;
          const detalleInfo = detalleDisponible[detailKey];

          const cumpleProceso =
            procesoSeleccionado === "TODOS" ||
            p.descripcion === procesoSeleccionado;

          if (cumpleProceso) {
            const tieneDetalleEnFecha =
              detalleInfo &&
              detalleInfo.fechas &&
              detalleInfo.fechas.some((fecha) => {
                const fechaConvertida = convertirFecha(fecha);
                return fechaConvertida === fechaSeleccionada;
              });

            const tiempo =
              p.numeropiedras === ".0000" || p.numeropiedras == null
                ? 0
                : parseFloat(p.numeropiedras);

            return tieneDetalleEnFecha && tiempo <= 0;
          }
          return false;
        });

        return allStdZero || hasChildWarning;
      });
    }

    if (filterFirstTime) {
      arr = arr.filter(([, data]) => data.isFirstTime === "Y");
    }

    return arr;
  }, [
    referencias,
    debouncedSearchTerm,
    filterRealSinStand,
    filterFirstTime,
    fechaSeleccionada,
    procesoSeleccionado,
    detalleDisponible,
  ]);

  const referenciasPaginadas = useMemo(() => {
    const inicio = (paginaActual - 1) * porPagina;
    return referenciasFiltradas.slice(inicio, inicio + porPagina);
  }, [referenciasFiltradas, paginaActual]);

  const expandirReferencia = async (ref) => {
    if (referenciaExpandida === ref) {
      setReferenciaExpandida(null);
      return;
    }
    setReferenciaExpandida(ref);
  };

  const handleInputChange = (ref, codigo, value) => {
    const key = `${ref}-${codigo}`;
    setInputsValores((prev) => ({ ...prev, [key]: value }));
  };

  const handleMetalChange = (ref, codigo, metal) => {
    const key = `${ref}-${codigo}`;
    setMetalSeleccionadoPorProceso((prev) => ({ ...prev, [key]: metal }));
  };

  const actualizarTiempo = async (ref, codigo) => {
    const key = `${ref}-${codigo}`;
    const valor = inputsValores[key];
    const metalSeleccionado = metalSeleccionadoPorProceso[key] || "Oro";

    if (!valor || isNaN(valor)) {
      notify.warn("Introduce un valor válido para el tiempo");
      return;
    }

    setLoadingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await insertNewTheoreticalTime(
        "insertTheoreticaltime",
        SCREEN_IDS.GENERAL,
        ref,
        codigo,
        valor,
        metalSeleccionado
      );

      if (response && response.status === "success") {
        setHighlightKey(key);
        notify.success(`Tiempo actualizado correctamente para ${metalSeleccionado}`);
      } else {
        console.error("Fallo en la actualización:", response);
        notify.error("Error al actualizar el tiempo");
      }
    } catch (error) {
      console.error("Error en insertNewTheoreticalTime:", error);
      notify.error("Error en el servidor");
    } finally {
      setLoadingKeys((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (isLoading) {
    return (
      <>
        {/* Skeletons para los controles superiores */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
          <Skeleton variant="rectangular" width={250} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
        </Box>

        {/* Skeletons para la tabla */}
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    textAlign: "center",
                    padding: "4px 8px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    backgroundColor: BACKGROUND_COLORS.TABLE_HEADER,
                  }}>
                  <Skeleton variant="text" width="60%" height={24} sx={{ mx: "auto" }} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(SKELETON_CONFIG.rows)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell
                    colSpan={3}
                    style={{
                      padding: "4px 8px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : BACKGROUND_COLORS.CARD || "#f5f5f5",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton variant="text" width={SKELETON_CONFIG.widthLabel} height={SKELETON_CONFIG.height} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            width: 250,
            height: 40,
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
          }}>
          <InputBase
            type='date'
            value={fechaSeleccionada || ""}
            onChange={(e) => setFechaSeleccionada(e.target.value)}
            sx={{
              flex: 1,
              height: "100%",
              padding: "8px",
            }}
          />
          <IconButton
            onClick={() => setFechaSeleccionada(null)}
            sx={{ p: "8px" }}
            title='Mostrar todas las fechas'
            disabled={!fechaSeleccionada}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Paper>

        <Paper sx={{ minWidth: 150 }}>
          <FormControl fullWidth size='small'>
            <InputLabel>Proceso</InputLabel>
            <Select
              value={procesoSeleccionado}
              label='Proceso'
              onChange={(e) => setProcesoSeleccionado(e.target.value)}>
              {procesosDisponibles.map((proceso) => (
                <MenuItem key={proceso} value={proceso}>
                  {proceso === "TODOS" ? "🔄 Todos" : `⚙️ ${proceso}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        <Paper sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
                          <span style={{ fontSize: "14px", color: TEXT_COLORS.SECONDARY }}>
            Total: {Object.keys(referencias).length} | Filtradas:{" "}
            {referenciasFiltradas.length} | Páginas:{" "}
            {referenciasPaginadas.length}
          </span>
        </Paper>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  textAlign: "center",
                  padding: "4px 8px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  backgroundColor: BACKGROUND_COLORS.TABLE_HEADER,
                }}>
                Referencia - {fechaSeleccionada || "Todas las fechas"}
                {procesoSeleccionado !== "TODOS" && ` - ${procesoSeleccionado}`}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {referenciasPaginadas.map(([referencia, data]) => {
              const procesos = data.procesos || [];

              const allStdZero =
                data.hasRealData === "Y" &&
                procesos.every((p) => parseFloat(p.numeropiedras || 0) === 0);

              const hasChildWarning = procesos.some((p) => {
                const detailKey = `${referencia}|${p.descripcion}`;
                const detalleInfo = detalleDisponible[detailKey];

                const cumpleProceso =
                  procesoSeleccionado === "TODOS" ||
                  p.descripcion === procesoSeleccionado;

                if (cumpleProceso) {
                  const tieneDetalleEnFecha =
                    detalleInfo &&
                    detalleInfo.fechas &&
                    detalleInfo.fechas.some((fecha) => {
                      const fechaConvertida = convertirFecha(fecha);
                      return fechaConvertida === fechaSeleccionada;
                    });

                  const tiempo =
                    p.numeropiedras === ".0000" || p.numeropiedras == null
                      ? 0
                      : parseFloat(p.numeropiedras);

                  return tieneDetalleEnFecha && tiempo <= 0;
                }
                return false;
              });

              const showParentWarning = allStdZero || hasChildWarning;

              return (
                <React.Fragment key={referencia}>
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      onClick={() => expandirReferencia(referencia)}
                      style={{
                        position: "relative",
                        padding: "4px 8px",
                        cursor: "pointer",
                        height: "40px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}>
                      <div
                        style={{
                          position: "absolute",
                          left: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}>
                        <IconButton size='small' style={{ padding: 2 }}>
                          {referenciaExpandida === referencia ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </div>

                      <div
                        style={{
                          position: "relative",
                          display: "inline-flex",
                          alignItems: "center",
                        }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                          {referencia}
                        </span>
                        {imageUrls[referencia] && (
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenImage(imageUrls[referencia]);
                            }}
                            title='Ver imagen'
                            style={{
                              position: "absolute",
                              left: "100%",
                              marginLeft: 4,
                              padding: 6,
                            }}>
                            <IconPhoto fontSize='small' />
                          </IconButton>
                        )}
                      </div>

                      <div
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          display: "flex",
                          gap: 4,
                        }}>
                        {showParentWarning && (
                          <IconWarning fontSize='small' color='error' />
                        )}
                        {data.isFirstTime === "Y" && (
                          <IconFirstTime fontSize='small' color='primary' />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      colSpan={3}
                      style={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse
                        in={referenciaExpandida === referencia}
                        timeout='auto'
                        unmountOnExit>
                        <Table
                          size='small'
                          style={{ tableLayout: "fixed", width: "100%" }}>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                style={{
                                  textAlign: "center",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}>
                                Proceso
                              </TableCell>
                              <TableCell
                                style={{
                                  textAlign: "center",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}>
                                Tiempo Estándar
                              </TableCell>
                              <TableCell
                                style={{
                                  textAlign: "center",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}>
                                Media Real
                              </TableCell>
                              <TableCell
                                style={{
                                  textAlign: "center",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}>
                                Metal
                              </TableCell>
                              <TableCell
                                style={{
                                  textAlign: "center",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}>
                                Actualizar
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {procesos.map((proceso) => {
                              const detailKey = `${referencia}|${proceso.descripcion}`;
                              const inputKey = `${referencia}-${proceso.codigo}`;

                              const detalleInfo = detalleDisponible[detailKey];

                              const tieneDetalle =
                                detalleInfo &&
                                detalleInfo.fechas &&
                                detalleInfo.fechas.length > 0;

                              const esLineaActual =
                                detalleInfo &&
                                detalleInfo.fechas &&
                                detalleInfo.fechas.some((fecha) => {
                                  const fechaConvertida = convertirFecha(fecha);
                                  return fechaConvertida === fechaSeleccionada;
                                }) &&
                                (procesoSeleccionado === "TODOS" ||
                                  proceso.descripcion === procesoSeleccionado);

                              const tiempo =
                                proceso.numeropiedras === ".0000" ||
                                proceso.numeropiedras === null
                                  ? 0
                                  : parseFloat(proceso.numeropiedras);

                              return (
                                <TableRow
                                  key={inputKey}
                                  style={{
                                    height: "36px",
                                    backgroundColor: esLineaActual
                                      ? '#c1d1d8'//color hardcodeado para la linea actual
                                      : highlightKey === inputKey
                                      ? "rgba(0,0,0,0.05)"
                                      : "transparent",
                                    transition: "background-color 0.3s",
                                  }}>
                                  <TableCell
                                    style={{
                                      position: "relative",
                                      padding: "4px 8px",
                                      height: "36px",
                                      textAlign: "center",
                                    }}>
                                    <IconButton
                                      size='small'
                                      onClick={() =>
                                        tieneDetalle &&
                                        onOpenModal(
                                          referencia,
                                          proceso.descripcion
                                        )
                                      }
                                      disabled={!tieneDetalle}
                                      style={{
                                        position: "absolute",
                                        left: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        padding: 2,
                                        margin: 0,
                                      }}
                                      title='Ver detalles'>
                                      <OpenIcon
                                        fontSize='small'
                                        style={{
                                          color: tieneDetalle
                                                            ? TEXT_COLORS.PRIMARY
                : BORDER_COLORS.LIGHT,
                                        }}
                                      />
                                    </IconButton>

                                    {tieneDetalle && tiempo <= 0 && (
                                      <IconWarning
                                        fontSize='small'
                                        color='error'
                                        titleAccess='Tiene tiempo real pero no estándar'
                                        style={{
                                          position: "absolute",
                                          left: 8 + 24 + 2 + 4,
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                        }}
                                      />
                                    )}

                                    <span
                                      style={{
                                        display: "inline-block",
                                        textAlign: "center",
                                      }}>
                                      {proceso.descripcion}
                                    </span>
                                  </TableCell>

                                  <TableCell style={{ textAlign: "center" }}>
                                    {tiempo} min
                                  </TableCell>

                                  <TableCell style={{ textAlign: "center" }}>
                                    {detalleInfo && detalleInfo.mediaReal ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                        }}>
                                        <span
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: STATUS_COLORS.SUCCESS,
                                          }}>
                                          {detalleInfo.mediaReal} min
                                        </span>
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            color: TEXT_COLORS.SECONDARY,
                                          }}>
                                          (
                                          {detalleInfo.totalRegistros -
                                            (detalleInfo.valoresEliminados ||
                                              0)}{" "}
                                          de {detalleInfo.totalRegistros} reg.)
                                        </span>
                                        {detalleInfo.valoresEliminados > 0 && (
                                          <span
                                            style={{
                                              fontSize: "10px",
                                              color: STATUS_COLORS.WARNING,
                                            }}>
                                            ⚠️ {detalleInfo.valoresEliminados}{" "}
                                            filtrados
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: TEXT_COLORS.TERTIARY,
                                          fontStyle: "italic",
                                        }}>
                                        Sin datos
                                      </span>
                                    )}
                                  </TableCell>

                                  <TableCell
                                    style={{
                                      textAlign: "center",
                                      padding: "4px",
                                    }}>
                                    <FormControl
                                      size='small'
                                      sx={{ minWidth: 80 }}>
                                      <Select
                                        value={
                                          metalSeleccionadoPorProceso[
                                            inputKey
                                          ] || "Oro"
                                        }
                                        onChange={(e) =>
                                          handleMetalChange(
                                            referencia,
                                            proceso.codigo,
                                            e.target.value
                                          )
                                        }
                                        sx={{
                                          fontSize: "12px",
                                          "& .MuiSelect-select": {
                                            padding: "4px 8px",
                                          },
                                        }}>
                                        <MenuItem
                                          value='Oro'
                                          sx={{ fontSize: "12px" }}>
                                          🟡 Oro
                                        </MenuItem>
                                        <MenuItem
                                          value='Plata'
                                          sx={{ fontSize: "12px" }}>
                                          ⚪ Plata
                                        </MenuItem>
                                        <MenuItem
                                          value='Platino'
                                          sx={{ fontSize: "12px" }}>
                                          ⚫ Platino
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </TableCell>

                                  <TableCell
                                    style={{
                                      padding: "4px 8px",
                                      height: "36px",
                                    }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 8,
                                        height: "100%",
                                        width: "100%",
                                      }}>
                                      {/* Lado izquierdo: controles */}
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          flex: "0 0 auto",
                                        }}>
                                        <TextField
                                          size='small'
                                          type='number'
                                          value={inputsValores[inputKey] || ""}
                                          onChange={(e) =>
                                            handleInputChange(
                                              referencia,
                                              proceso.codigo,
                                              e.target.value
                                            )
                                          }
                                          style={{ width: 80 }}
                                          InputProps={{
                                            inputProps: { step: "any" },
                                            style: {
                                              width: "100%",
                                              height: "36px",
                                              padding: "4px",
                                            },
                                          }}
                                        />

                                        <Button
                                          variant='contained'
                                          onClick={() =>
                                            actualizarTiempo(
                                              referencia,
                                              proceso.codigo
                                            )
                                          }
                                          disabled={loadingKeys[inputKey]}
                                          startIcon={
                                            loadingKeys[inputKey] ? (
                                              <CircularProgress size={16} />
                                            ) : null
                                          }
                                          sx={{
                                            bgcolor: BRAND_COLORS.SECONDARY,
                                            textTransform: "none",
                                            padding: "4px 8px",
                                            fontSize: "14px",
                                            minWidth: "90px",
                                            "&:hover": {
                                              bgcolor: BRAND_COLORS.PRIMARY_DARK,
                                            },
                                          }}>
                                          {loadingKeys[inputKey]
                                            ? "Guardando…"
                                            : "Actualizar"}
                                        </Button>
                                      </div>

                                      {/* Lado derecho: información */}
                                      {(() => {
                                        const prefijo = referencia.substring(
                                          0,
                                          5
                                        );
                                        const lookupKey = `${prefijo}-${proceso.codigo}`;
                                        const actualizada =
                                          lookupActualizadas[lookupKey];
                                        if (!actualizada) return null;

                                        const fecha = new Date(
                                          actualizada.fecha.date
                                        ).toLocaleDateString();
                                        const tiempo = parseFloat(
                                          actualizada.numeropiedras
                                        ).toFixed(1);

                                        return (
                                          <div
                                            style={{
                                              textAlign: "right",
                                              fontSize: "12px",
                                              color: TEXT_COLORS.PRIMARY,
                                              lineHeight: 1.2,
                                              flex: "1 1 auto",
                                              minWidth: 0,
                                              marginLeft: 8,
                                            }}>
                                            <div
                                              style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}>
                                              Actualizada:{" "}
                                              <strong>{fecha}</strong>
                                            </div>
                                            <div
                                              style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}>
                                              Tiempo: <strong>{tiempo}</strong>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "16px 0",
        }}>
        <Button
          variant='contained'
          onClick={() => setPaginaActual((prev) => Math.max(prev - 1, 1))}
          disabled={paginaActual === 1}
          style={{
            width: "100px",
            backgroundColor: BRAND_COLORS.SECONDARY,
            fontWeight: "bold",
          }}>
          Anterior
        </Button>
        <span
          style={{ alignSelf: "center", marginLeft: 10, lineHeight: "36px" }}>
          Página {paginaActual} de{" "}
          {Math.ceil(referenciasFiltradas.length / porPagina)}
        </span>
        <Button
          variant='contained'
          onClick={() =>
            setPaginaActual((prev) =>
              prev * porPagina < referenciasFiltradas.length ? prev + 1 : prev
            )
          }
          disabled={paginaActual * porPagina >= referenciasFiltradas.length}
          style={{
            marginLeft: 10,
            backgroundColor: BRAND_COLORS.SECONDARY,
            color: "white",
            fontWeight: "bold",
            width: "100px",
          }}>
          Siguiente
        </Button>
      </div>

      {/* Modal de imagen */}
      <Dialog
        open={Boolean(openImage)}
        onClose={handleCloseImage}
        maxWidth='md'
        fullWidth>
        <IconButton
          onClick={handleCloseImage}
          sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <img
            src={openImage}
            alt='Referencia'
            style={{ maxWidth: "100%", maxHeight: "80vh" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VistaAvanzadaReferencias;
